import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { DashboardService } from "services";
import { DashboardAgentFilterParams, DashboardMonthlyProgressResponse, DashboardScope } from "types";

import MonthlyProgress from "../admin/MonthlyProgress";

import shellClasses from "./SectionShell.module.css";
import classes from "./MonthlyProgressSection.module.css";

interface MonthlyProgressSectionProps {
  className?: string;
  scope?: DashboardScope;
  selectedAgentIds?: number[] | null;
}

interface MonthlyProgressMetrics {
  realizedAmount: number;
  targetAmount: number;
  projectedAmount: number;
  currency: "CHF",
}

const DEFAULT_MONTHLY_PROGRESS: MonthlyProgressMetrics = {
  realizedAmount: 0,
  targetAmount: 0,
  projectedAmount: 0,
  currency: "CHF",
};

const mapMonthlyProgressResponse = (
  data?: DashboardMonthlyProgressResponse,
): MonthlyProgressMetrics => ({
  realizedAmount: data?.realized ?? 0,
  targetAmount: data?.target ?? 0,
  projectedAmount: data?.projection ?? 0,
  currency: "CHF",
});

const formatCurrencyAmount = (value: number, locale: string, currency: string) =>
  `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)} ${currency}`;

const formatAmount = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);

const MonthlyProgressSection = ({
  className,
  scope = "admin",
  selectedAgentIds,
}: MonthlyProgressSectionProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "sr";
  const [progress, setProgress] = useState<MonthlyProgressMetrics>(DEFAULT_MONTHLY_PROGRESS);

  useEffect(() => {
    let mounted = true;
    const params: DashboardAgentFilterParams | undefined = selectedAgentIds?.length
      ? { agentIds: selectedAgentIds }
      : undefined;

    const loadProgress = async () => {
      try {
        const data = await DashboardService.getMonthlyProgress(scope, params);

        if (!mounted) {
          return;
        }

        setProgress(mapMonthlyProgressResponse(data));
      } catch {
        if (!mounted) {
          return;
        }

        setProgress(DEFAULT_MONTHLY_PROGRESS);
      }
    };

    loadProgress();

    return () => {
      mounted = false;
    };
  }, [scope, selectedAgentIds]);

  return (
    <section className={[shellClasses.root, className].filter(Boolean).join(" ")}>
      <div className={classes.content}>
        <MonthlyProgress
          title={t("dashboard.admin.progress.monthlyTitle")}
          value={progress.realizedAmount}
          target={progress.targetAmount}
          valueLabel={formatAmount(progress.realizedAmount, locale)}
          targetLabel={formatCurrencyAmount(
            progress.targetAmount,
            locale,
            progress.currency,
          )}
          projectionLabel={`${t("dashboard.admin.progress.projection")}: ${formatCurrencyAmount(
            progress.projectedAmount,
            locale,
            progress.currency,
          )}`}
        />
      </div>
    </section>
  );
};

export default MonthlyProgressSection;
